import {ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, ViewChild} from "@angular/core";
import {CommonModule} from "@angular/common";
import {ReactiveFormsModule, FormBuilder, Validators} from "@angular/forms";
import {Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {MarkdownModule} from "ngx-markdown";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatChipsModule} from "@angular/material/chips";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatTooltipModule} from "@angular/material/tooltip";
import {TextFieldModule} from "@angular/cdk/text-field";
import {firstValueFrom} from "rxjs";

import {AiChatActions} from "../../store/ai-chat/ai-chat.actions";
import {
  selectAiChatMessages,
  selectAiChatIsStreaming,
  selectAiChatStreamingText,
  selectAiChatToolCallStatus,
  selectAiChatError,
} from "../../store/ai-chat/ai-chat.selectors";
import {selectAiChatContext} from "../../store/ai-chat/ai-chat-context.selector";
import {AiChatContext} from "../../generated-sources/api/ai/model/ai-chat-context";
import {AiEntityReference} from "../../generated-sources/api/ai/model/ai-entity-reference";

const MIN_PANEL_WIDTH = 380;
const MAX_PANEL_WIDTH = 800;
const DEFAULT_PANEL_WIDTH = 400;

@Component({
  selector: "valumize-ai-chat-panel",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MarkdownModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatProgressBarModule,
    MatTooltipModule,
    TextFieldModule,
  ],
  templateUrl: "./ai-chat-panel.component.html",
  styleUrl: "./ai-chat-panel.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatPanelComponent {
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);

  @ViewChild("messagesContainer") messagesContainer!: ElementRef<HTMLDivElement>;

  readonly messages$ = this.store.select(selectAiChatMessages);
  readonly isStreaming$ = this.store.select(selectAiChatIsStreaming);
  readonly streamingText$ = this.store.select(selectAiChatStreamingText);
  readonly toolCallStatus$ = this.store.select(selectAiChatToolCallStatus);
  readonly error$ = this.store.select(selectAiChatError);

  static readonly MAX_MESSAGE_LENGTH = 10000;

  readonly chatForm = this.fb.group({
    message: ["", [Validators.maxLength(AiChatPanelComponent.MAX_MESSAGE_LENGTH)]],
  });

  readonly maxMessageLength = AiChatPanelComponent.MAX_MESSAGE_LENGTH;
  panelWidth = DEFAULT_PANEL_WIDTH;
  dragging = false;

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.dragging = true;
  }

  @HostListener("document:mousemove", ["$event"])
  onMouseMove(event: MouseEvent): void {
    if (!this.dragging) return;
    const newWidth = window.innerWidth - event.clientX;
    this.panelWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth));
    document.documentElement.style.setProperty("--ai-chat-width", `${this.panelWidth}px`);
  }

  @HostListener("document:mouseup")
  onMouseUp(): void {
    this.dragging = false;
  }

  async sendMessage(): Promise<void> {
    const message = this.chatForm.get("message")?.value?.trim();
    if (!message) return;

    const context = await firstValueFrom(this.store.select(selectAiChatContext));
    this.store.dispatch(AiChatActions.sendMessage({message, context: this.buildFullContext(context)}));
    this.chatForm.reset();
    this.scrollToBottom();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  closePanel(): void {
    this.store.dispatch(AiChatActions.closePanel());
  }

  navigateToEntity(ref: AiEntityReference): void {
    this.router.navigate([ref.route]);
  }

  onMessageClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName === "A") {
      const href = target.getAttribute("href");
      if (href && href.startsWith("/desk/")) {
        event.preventDefault();
        const [path, queryString] = href.split("?");
        // Strip /desk/ base href — Angular Router expects paths without it
        const routePath = path.substring(5); // '/desk/documentation' → '/documentation'
        const queryParams: Record<string, string> = {};
        if (queryString) {
          queryString.split("&").forEach(param => {
            const [key, value] = param.split("=");
            queryParams[decodeURIComponent(key)] = decodeURIComponent(value || "");
          });
        }
        this.router.navigate([routePath], {queryParams});
      }
    }
  }

  async sendSuggestedAction(triggerPrompt: string): Promise<void> {
    const context = await firstValueFrom(this.store.select(selectAiChatContext));
    this.store.dispatch(AiChatActions.sendMessage({message: triggerPrompt, context: this.buildFullContext(context)}));
  }

  async retryLastMessage(): Promise<void> {
    const messages = await firstValueFrom(this.store.select(selectAiChatMessages));
    const lastUserMessage = [...messages].reverse().find(m => m.role === "user");
    if (lastUserMessage) {
      const context = await firstValueFrom(this.store.select(selectAiChatContext));
      this.store.dispatch(AiChatActions.sendMessage({message: lastUserMessage.content, context: this.buildFullContext(context)}));
    }
  }

  private buildFullContext(storeContext: AiChatContext): AiChatContext {
    const url = this.router.url;
    const queryParams = this.router.parseUrl(url).queryParams;

    return {
      ...storeContext,
      currentRoute: url.split("?")[0],
      fundId: storeContext.fundId ?? this.parseNumber(queryParams["fundId"]),
      fundReportId: storeContext.fundReportId ?? this.parseNumber(queryParams["fundReportId"]),
      fundValuationId: storeContext.fundValuationId ?? this.parseNumber(queryParams["fundValuationId"]),
      dealId: storeContext.dealId ?? this.parseNumber(queryParams["dealId"]),
    };
  }

  private parseNumber(value: string | undefined): number | undefined {
    if (!value) return undefined;
    const num = Number(value);
    return isNaN(num) ? undefined : num;
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      const container = this.messagesContainer?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }
}
